import { dehydrate, QueryClient } from '@tanstack/react-query';
import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next';
import Head from 'next/head';
import React, { useEffect, useState } from 'react';

import { getWebinar } from '@/api/webinar';
import Footer from '@/components/common/Footer';
import { GNB } from '@/components/gnb';
import WebinarToast from '@/components/webinar/WebinarToast';
import {
  useUpdateWebinarUserOrientation,
  useWebinar,
  useWebinarUser,
} from '@/hooks/react-query/webinar';
import { getOnlineUserIdFromServerCookie } from '@/lib/utils/cookie';
import WebinarApplicationModal from '@/pages/webinar/[title]/components/WebinarApplicationModal';
import WebinarFloatingCta from '@/pages/webinar/[title]/components/WebinarFloatingCta';
import Template from '@/pages/webinar/[title]/template';

type ServerSideProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const WebinarDetailPage = ({ title, ssrOnlineUserId }: ServerSideProps) => {
  const { webinar, recommends } = useWebinar(title);
  const { webinarUser } = useWebinarUser(webinar._id, ssrOnlineUserId);
  const { mutate } = useUpdateWebinarUserOrientation();
  const [showApplicationModal, setShowApplicationModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (!webinarUser) {
      return;
    }
    let target = {};
    let flag = false;
    if (
      webinar.status === 'upcoming' &&
      !webinarUser.orientation.upcomingViewed
    ) {
      flag = true;
      target = { upcomingViewed: true };
    }
    if (webinar.status === 'onair' && !webinarUser.orientation.onairViewed) {
      flag = true;
      target = { onairViewed: true };
    }
    if (webinar.status === 'done' && !webinarUser.orientation.doneViewed) {
      flag = true;
      target = { doneViewed: true };
    }
    if (flag) {
      mutate({
        webinarId: webinar._id,
        onlineUserId: ssrOnlineUserId,
        orientation: {
          ...webinarUser.orientation,
          ...target,
        },
      });
    }
  }, [webinarUser]);

  useEffect(() => {
    document.body.style.overflow = showApplicationModal ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showApplicationModal]);

  return (
    <>
      <Head>
        <meta
          property='og:title'
          content='항해 웨비나에서 성장의 노하우를 얻어가세요.'
        />
        <meta
          property='og:description'
          content='당신도 성장할 수 있습니다. 방법을 모를 뿐입니다. 항해 웨비나에서 성장의 노하우를 공유 받으세요. 커리어를 넘어, 삶을 바꾸는 당신만의 항해는 지금부터입니다.'
        />
        <meta
          name='keywords'
          content='항해99, 항해플러스, 웨비나, 성공, 직장인, 인사이트, 커리어, 노하우, 개발, PM, PO, 데이터분석, 직무, 강연,'
        />
        <meta
          name='viewport'
          content='user-scalable=no,width=device-width,initial-scale=1,shrink-to-fit=no'
        />
      </Head>
      <GNB />
      <Template
        webinar={webinar}
        recommends={recommends}
        onlineUserId={ssrOnlineUserId}
      />
      {webinar.status === 'upcoming' && (
        <WebinarFloatingCta
          webinar={webinar}
          onlineUserId={ssrOnlineUserId}
          showApplicationModal={showApplicationModal}
          setShowApplicationModal={setShowApplicationModal}
        />
      )}
      <WebinarToast />
      {showApplicationModal && (
        <WebinarApplicationModal
          webinar={webinar}
          onlineUserId={ssrOnlineUserId}
          setShowApplicationModal={setShowApplicationModal}
        />
      )}
      <Footer />
    </>
  );
};

export const getServerSideProps = async ({
  query,
  req,
}: GetServerSidePropsContext) => {
  const { title } = query;
  const queryClient = new QueryClient();

  await queryClient.fetchQuery({
    queryKey: ['webinar', title],
    queryFn: () => getWebinar(title as string),
    staleTime: 300000,
    gcTime: 300000,
  });

  const ssrOnlineUserId = getOnlineUserIdFromServerCookie(req);
  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      title: title as string,
      ssrOnlineUserId,
    },
  };
};

export default WebinarDetailPage;
